/**
 * Implement a class that represents a Google Gemini AI client
 * and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 * - streamThread(thread: Thread): AsyncGenerator<string, Thread> - stream a message to the LLM
 */

import { GenerateContentResponse, GoogleGenAI } from "@google/genai";
import { nanoid } from 'nanoid';
import { zodToJsonSchema } from "zod-to-json-schema";
import {
  Message,
  type MessageRole,
  type ToolCall
} from "../message";
import type { Thread } from "../thread";
import { traceable } from "../tracing";
import { BaseModelDriver } from "./base";

// Re-export Type for use in other files
export { Type } from "@google/genai";

/**
 * Available Gemini model options
 */
export type GeminiModel = 
  // Gemini 2.5 models
  | "gemini-2.5-flash"
  | "gemini-2.5-pro-exp-03-25"  
  
  // Gemini 2.0 models
  | "gemini-2.0-flash-lite"
  | "gemini-2.0-flash-thinking-exp-01-21"
  
  // Gemini 1.5 models
  | "gemini-1.5-flash"
  | "gemini-1.5-pro"
  
  // Gemini 1.0 models
  | "gemini-1.0-pro"

/**
 * Schema for structured output
 */
export interface Schema {
  type: "string" | "integer" | "number" | "boolean" | "array" | "object";
  format?: string;
  description?: string;
  nullable?: boolean;
  enum?: string[];
  maxItems?: string;
  minItems?: string;
  properties?: Record<string, Schema>;
  required?: string[];
  propertyOrdering?: string[];
  items?: Schema;
}

const DEFAULT_MODEL: GeminiModel = "gemini-2.5-flash";
const MAX_PROMPT_CHARACTERS = 320_000;

interface GeminiConfig {
  model?: GeminiModel | string;
  temperature?: number;
  maxTokens?: number;
  apiKey?: string;
  cache?: boolean;
  /**
   * Whether to enable tracing
   * @default false
   */
  trace?: boolean;
  
  /**
   * Additional metadata for tracing
   */
  traceMetadata?: Record<string, unknown>;

  /**
   * Schema for structured output
   */
  responseSchema?: Schema;
}


export class GeminiThreadDriver extends BaseModelDriver {
  private readonly genAI: GoogleGenAI;
  private readonly model: GeminiModel | string;
  private readonly temperature: number;
  private readonly maxTokens: number;
  private readonly cache: boolean;
  private readonly responseSchema?: Schema;

  /**
   * Get list of available model names for easy reference
   */
  public static getAvailableModels(): GeminiModel[] {
    return [
      // Gemini 2.5 models
      "gemini-2.5-flash",
      "gemini-2.5-pro-exp-03-25",
      
      // Gemini 2.0 models
      "gemini-2.0-flash-lite",
      "gemini-2.0-flash-thinking-exp-01-21",
      
      // Gemini 1.5 models
      "gemini-1.5-flash",
      "gemini-1.5-pro",
      
      // Gemini 1.0 models
      "gemini-1.0-pro"
    ];
  }

  constructor(config: GeminiConfig = {}) {
    super();
    
    // Check for required API key if not provided in config
    const apiKey = config.apiKey || process.env.GOOGLE_API_KEY;
    
    if (!apiKey) {
      throw new Error("Google API Key must be provided in config or as GOOGLE_API_KEY environment variable");
    }

    // Create a base client
    const baseClient = new GoogleGenAI({apiKey});

    // Only wrap with tracing if trace is enabled (default to false)
    if (config.trace ?? false) {
      // Create a proxy around the Google GenAI client to intercept API calls
      this.genAI = new Proxy(baseClient, {
        get: (target, prop, receiver) => {
          const value = Reflect.get(target, prop, receiver);
          
          // Handle nested properties (models.generateContent)
          if (prop === 'models' && value && typeof value === 'object') {
            return new Proxy(value, {
              get: (modelsTarget, modelsProp, modelsReceiver) => {
                const modelsValue = Reflect.get(modelsTarget, modelsProp, modelsReceiver);
                
                // Wrap the generateContent method with tracing
                if (modelsProp === 'generateContent' && typeof modelsValue === 'function') {
                  return traceable(modelsValue.bind(modelsTarget), {
                    run_type: "llm",
                    name: "gemini",
                    ...config.traceMetadata,
                    aggregator: (args: any[], result: any) => {
                      // Extract relevant data for tracing
                      const params = args[0] || {};
                      
                      // Return metrics and data
                      return {
                        model: params.model,
                        input: {
                          contents: params.contents,
                          temperature: params.generationConfig?.temperature,
                          max_tokens: params.generationConfig?.maxOutputTokens,
                          tools: params.tools ? 
                            (params.tools.functionDeclarations?.length || 0) : 0,
                        },
                        output: result.candidates?.[0]?.content,
                        metrics: result.usageMetadata ? {
                          prompt_tokens: result.usageMetadata.promptTokenCount,
                          completion_tokens: result.usageMetadata.candidatesTokenCount,
                          total_tokens: (result.usageMetadata.promptTokenCount || 0) + 
                            (result.usageMetadata.candidatesTokenCount || 0),
                        } : undefined,
                        finish_reason: result.candidates?.[0]?.finishReason
                      };
                    }
                  });
                }
                
                // Wrap the generateContentStream method with tracing
                if (modelsProp === 'generateContentStream' && typeof modelsValue === 'function') {
                  return traceable(modelsValue.bind(modelsTarget), {
                    run_type: "llm",
                    name: "gemini",
                    ...config.traceMetadata,
                    aggregator: (args: any[], _result: any) => {
                      // Extract relevant data for tracing
                      const params = args[0] || {};
                      
                      // For streaming responses, we only log the input
                      return {
                        model: params.model,
                        input: {
                          contents: params.contents,
                          temperature: params.generationConfig?.temperature,
                          max_tokens: params.generationConfig?.maxOutputTokens,
                          tools: params.tools ? 
                            (params.tools.functionDeclarations?.length || 0) : 0,
                        },
                        streaming: true
                      };
                    }
                  });
                }
                
                return modelsValue;
              }
            });
          }
          
          return value;
        }
      });
    } else {
      this.genAI = baseClient;
    }

    this.model = config.model || DEFAULT_MODEL;
    this.temperature = config.temperature || 0.7;
    this.maxTokens = config.maxTokens || 4096;
    this.cache = config.cache || false;
    this.responseSchema = config.responseSchema;
  }

  /**
   * Convert messages to Gemini API format
   */
  private convertMessagesToGeminiFormat(messages: Message[]): {
    contents: any[];
    system?: string;
  } {
    const contents: any[] = [];
    let system: string | undefined;

    messages.forEach((message) => {
      // Handle system messages separately
      if (message.role === "system") {
        system = message.content || "";
        return;
      }

      // Skip tool messages, as they're represented as tool responses
      if (message.role === "tool") {
        return;
      }

      const parts: any[] = [];

      // Add text content if present
      if (message.content) {
        parts.push({
          text: message.content
        });
      }

      // Add attachment content if present
      if (message.attachments && message.attachments.length > 0) {
        for (const attachment of message.attachments) {
          if (attachment.type === "image" && attachment.source.uri) {
            parts.push({
              inlineData: {
                mimeType: `image/${attachment.format}`,
                data: attachment.source.uri
              }
            });
          }
          // Other attachment types can be handled here
        }
      }

      // Map roles from our system to Gemini API
      const role = message.role === "assistant" ? "model" : "user";

      // Handle tool calls for the assistant
      if (message.toolCalls && message.toolCalls.length > 0 && role === "model") {
        for (const toolCall of message.toolCalls) {
          parts.push({
            functionCall: {
              name: toolCall.name,
              args: toolCall.arguments
            }
          });
        }
      }

      // Handle tool results for the user
      if (message.toolResults && message.toolResults.length > 0 && role === "user") {
        for (const toolResult of message.toolResults) {
          parts.push({
            functionResponse: {
              name: toolResult.name,
              response: {
                name: toolResult.name,
                content: toolResult.result
              }
            }
          });
        }
      }

      // Only add the message if it has parts
      if (parts.length > 0) {
        contents.push({
          role,
          parts
        });
      }
    });

    return {
      contents,
      system
    };
  }

  /**
   * Parse tool calls from the Gemini API response
   */
  private parseToolCalls(parts: any[]): ToolCall[] {
    const toolCalls: ToolCall[] = [];
    
    if (parts) {
      for (const part of parts) {
        if (part.functionCall && typeof part.functionCall.name === 'string') {
          toolCalls.push({
            name: part.functionCall.name,
            toolUseId: nanoid(15),
            arguments: part.functionCall.args || {}
          });
        }
      }
    }
    
    return toolCalls;
  }

  /**
   * Send the thread state to the LLM and process the response
   */
  public async sendThread(thread: Thread): Promise<Thread> {
    const messages = thread.all();
    if (messages.length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    const totalContent = messages.map((m) => m.content || "").join("\n");
    if (totalContent.length > MAX_PROMPT_CHARACTERS) {
      throw new Error("The thread content is too long");
    }

    try {
      const { contents, system } = this.convertMessagesToGeminiFormat(messages);
      
      const tools = thread.getTools();
      const functionDeclarations = tools.length > 0 ? this.createToolConfig(tools) : undefined;

      // Create the configuration object with proper typing
      const generateContentConfig: any = {
        contents,
        generationConfig: {
          temperature: this.temperature,
          maxOutputTokens: this.maxTokens,
          topP: 0.95,
        },
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_ONLY_HIGH",
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_ONLY_HIGH",
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_ONLY_HIGH",
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_ONLY_HIGH",
          },
        ]
      };

      // Add tools and tool config if present
      if (functionDeclarations) {
        generateContentConfig.tools = {
          functionDeclarations
        };
        generateContentConfig.toolConfig = {
          toolChoice: thread.toolChoice === "any" ? "any" : "auto"
        };
      }

      // Add structured output configuration if responseSchema is defined
      if (this.responseSchema) {
        generateContentConfig.responseMimeType = 'application/json';
        generateContentConfig.responseSchema = this.responseSchema;
      }

      // Use the models property to generate content
      const contentParams: any = {
        model: this.model,
        ...generateContentConfig
      };
      
      // Add system parameter directly to the params object
      if (system) {
        contentParams.system = system!;
      }
      
      const result = await this.genAI.models.generateContent(contentParams);

      if (!result.candidates || result.candidates.length === 0) {
        throw new Error("No response received from Gemini API");
      }

      const candidate = result.candidates[0];
      if (!candidate || !candidate.content) {
        throw new Error("Invalid candidate response from Gemini API");
      }

      const content = candidate.content;
      
      // Get the text content
      let responseText = "";
      if (content.parts) {
        for (const part of content.parts) {
          if (part.text) {
            responseText += part.text;
          }
        }
      }

      // Get tool calls
      const toolCalls = this.parseToolCalls(content.parts || []);

      const responseMessage = new Message({
        content: responseText,
        role: "assistant" as MessageRole,
        toolCalls: toolCalls,
        cache: this.cache,
      });

      thread.push(responseMessage);

      // Log usage metrics if available
      if (result.usageMetadata) {
        const inputTokens = result.usageMetadata.promptTokenCount || 0;
        const outputTokens = result.usageMetadata.candidatesTokenCount || 0;
        
        this.debug("Gemini API usage metrics", {
          inputTokens,
          outputTokens,
          totalTokens: inputTokens + outputTokens,
        });
      }

      return thread;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      this.error(
        `Failed to send thread: ${errorMessage} (thread length: ${thread.all().length})`
      );
      throw error;
    }
  }

  /**
   * Create tool config for Gemini API
   */
  private createToolConfig(tools: any[]): any[] {
    return tools.map((tool) => ({
      name: tool.getName(),
      description: tool.getDescription(),
      parameters: zodToJsonSchema(tool.getParameters())
    }));
  }

  /**
   * Stream the thread state to the LLM and process the response chunk by chunk
   */
  public async *streamThread(thread: Thread): AsyncGenerator<{ stream: AsyncGenerator<string, void>, message: Message }, Thread> {
    const messages = thread.all();
    if (messages.length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    const totalContent = messages.map((m) => m.content || "").join("\n");
    if (totalContent.length > MAX_PROMPT_CHARACTERS) {
      throw new Error("The thread content is too long");
    }

    try {
      const { contents, system } = this.convertMessagesToGeminiFormat(messages);
      
      const tools = thread.getTools();
      const functionDeclarations = tools.length > 0 ? this.createToolConfig(tools) : undefined;

      const initialMessage = new Message({
        content: "",
        role: "assistant",
        toolCalls: [],
        cache: this.cache,
      });

      // Create the configuration object with proper typing
      const generateContentConfig: any = {
        contents,
        generationConfig: {
          temperature: this.temperature,
          maxOutputTokens: this.maxTokens,
          topP: 0.95,
        },
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_ONLY_HIGH",
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_ONLY_HIGH",
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_ONLY_HIGH",
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_ONLY_HIGH",
          },
        ]
      };

      // Add tools and tool config if present
      if (functionDeclarations) {
        generateContentConfig.tools = {
          functionDeclarations
        };
        generateContentConfig.toolConfig = {
          toolChoice: thread.toolChoice === "any" ? "any" : "auto"
        };
      }

      // Add structured output configuration if responseSchema is defined
      // Note: Structured output may not work well with streaming
      if (this.responseSchema) {
        generateContentConfig.responseMimeType = 'application/json';
        generateContentConfig.responseSchema = this.responseSchema;
      }

      // Use the models property to generate content
      const contentParams: any = {
        model: this.model,
        ...generateContentConfig
      };
      
      // Add system parameter directly to the params object
      if (system) {
        contentParams.system = system!;
      }
      
      const streamingResponse = await this.genAI.models.generateContentStream(contentParams);
      
      const stream = this.createStreamProcessor(streamingResponse, initialMessage);
      yield { stream, message: initialMessage };

      thread.push(initialMessage);
      return thread;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      this.error(
        `Failed to stream thread: ${errorMessage} (thread length: ${thread.all().length})`
      );
      throw error;
    }
  }

  /**
   * Create a stream processor for handling Gemini API streaming responses
   */
  private async *createStreamProcessor(
    streamingResponse: AsyncGenerator<GenerateContentResponse>,
    initialMessage: Message
  ): AsyncGenerator<string, void> {
    let accumulatedText = "";
    let toolCalls: ToolCall[] = [];

    for await (const chunk of streamingResponse) {
      // Check if there's content in the result
      if (!chunk.candidates?.[0]?.content?.parts) continue;
      
      const parts = chunk.candidates[0].content.parts || [];
      
      // Process text chunks
      for (const part of parts) {
        if (part.text) {
          const newText = part.text;
          
          // Only yield if there's new text
          if (newText && !accumulatedText.endsWith(newText)) {
            accumulatedText += newText;
            
            // Update the message content
            (initialMessage as any)._content = accumulatedText;
            
            yield newText;
          }
        }
        
        // Store function calls (tool calls) for later processing
        if (part.functionCall && typeof part.functionCall.name === 'string') {
          toolCalls.push({
            name: part.functionCall.name,
            toolUseId: nanoid(15),
            arguments: part.functionCall.args || {}
          });
          
          // Update tool calls in the message
          (initialMessage as any)._toolCalls = toolCalls;
        }
      }
    }
  }
} 